// src/App.js
import React, { useEffect, useState, useCallback } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import { supabase } from './supabaseClient'
import Layout from './components/Layout'

// Pages
import AdminDashboard from './pages/AdminDashboard'
import VehicleList from './pages/Vehicles'
import Reports from './pages/Reports'
import PaymentsPage from './pages/Payments'
import MissingPaymentsReport from './pages/MissingPaymentsReport'
import ProfitLossReport from './pages/ProfitLossReport'
import SpecificVehicleReport from './pages/SpecificVehicleReport'
import UserApprovals from './pages/UserApprovals'

function SpinnerFullscreen() {
  return (
    <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  )
}

function LoginView({
  error, setError,
  showRegister, setShowRegister,
  email, setEmail, password, setPassword, name, setName,
  onLogin, onRegister, onGoogleLogin,
  busy
}) {
  const inputStyle = {
    display: 'block', marginBottom: 10, width: '100%', padding: '10px',
    borderRadius: '6px', border: '1px solid #ccc'
  }
  const buttonStyle = (bg) => ({
    width: '100%', padding: '10px', marginBottom: 10, background: bg,
    color: 'white', border: 'none', borderRadius: '6px',
    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.8 : 1
  })

  return (
    <Box sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 6, boxShadow: 3, borderRadius: 3, backgroundColor: 'white' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>
        {showRegister ? 'Register' : 'Login'}
      </h2>
      {error && <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>}

      {showRegister && (
        <input
          type="text" placeholder="Full Name" value={name}
          onChange={(e) => setName(e.target.value)} style={inputStyle}
        />
      )}
      <input
        type="email" placeholder="Email" value={email}
        onChange={(e) => setEmail(e.target.value)} style={inputStyle}
      />
      <input
        type="password" placeholder="Password" value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !busy) onLogin() }}
        style={inputStyle}
      />

      {!showRegister ? (
        <>
          <button onClick={onLogin} style={buttonStyle('#1976d2')} disabled={busy}>
            {busy ? 'Logging in…' : 'Login'}
          </button>
          <button onClick={() => setShowRegister(true)} style={buttonStyle('#444')} disabled={busy}>
            Create Account
          </button>
        </>
      ) : (
        <>
          <button onClick={onRegister} style={buttonStyle('#1976d2')} disabled={busy}>
            {busy ? 'Working…' : 'Register'}
          </button>
          <button onClick={() => setShowRegister(false)} style={buttonStyle('#777')} disabled={busy}>
            Back to Login
          </button>
        </>
      )}

      <button onClick={onGoogleLogin} style={buttonStyle('#4285F4')} disabled={busy}>
        {busy ? 'Opening Google…' : 'Sign in with Google'}
      </button>
    </Box>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [role, setRole] = useState('user')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showRegister, setShowRegister] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [authBusy, setAuthBusy] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  // Helper: safe expiry check WITHOUT any dayjs plugins
  const notExpired = (expires_at) => {
    if (!expires_at) return true
    // Compare timestamps (end of day)
    const end = dayjs(expires_at).endOf('day').valueOf()
    return Date.now() <= end
  }

  // Approval check (navigation only; no signOut here)
  const checkSession = useCallback(async () => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const userId = s?.user?.id

      if (!s || !userId) {
        setSession(null)
        setRole('user')
        if (location.pathname !== '/login') navigate('/login', { replace: true })
        return
      }

      // Robust fetch for approval row (no plugins, no maybeSingle)
      let accessRow = null
      let accessErr = null
      const resp = await supabase
        .from('approved_users')
        .select('role, is_approved, expires_at')
        .eq('user_id', userId)
        .limit(1)

      if (resp.error) {
        accessErr = resp.error
      } else {
        accessRow = (resp.data && resp.data.length > 0) ? resp.data[0] : null
      }

      const approved = !!accessRow?.is_approved
      const okExpiry = notExpired(accessRow?.expires_at)

      if (approved && okExpiry) {
        setSession(s)
        setRole(accessRow?.role || 'user')
        if (location.pathname === '/' || location.pathname === '/login') {
          navigate('/dashboard', { replace: true })
        }
        return
      }

      if (!accessRow && accessErr) {
        // RLS blocked read → allow temp access
        setSession(s)
        setRole('user')
        if (location.pathname === '/' || location.pathname === '/login') {
          navigate('/dashboard', { replace: true })
        }
        return
      }

      if (!accessRow) {
        // No row yet → temp access
        setSession(s)
        setRole('user')
        if (location.pathname === '/' || location.pathname === '/login') {
          navigate('/dashboard', { replace: true })
        }
        return
      }

      // Explicitly unapproved or expired
      setSession(null)
      setRole('user')
      setError('Your access is not approved or has expired.')
      navigate('/login', { replace: true })
    } catch (e) {
      console.error('[Auth] checkSession failed:', e)
      setSession(null)
      setRole('user')
      setError('Authentication check failed. Please try again.')
      if (location.pathname !== '/login') navigate('/login', { replace: true })
    } finally {
      setLoading(false)
    }
  }, [location.pathname, navigate])

  // Initial check + auth listener
  useEffect(() => {
    let unsub
    const init = async () => {
      await checkSession()
      const { data } = supabase.auth.onAuthStateChange(async (_event, sess) => {
        if (sess?.user) {
          // Optimistic UI update so it doesn't “wait until refresh”
          setSession(sess)
          if (location.pathname === '/' || location.pathname === '/login') {
            navigate('/dashboard', { replace: true })
          }
          // Verify approval/expiry in background
          checkSession()
        } else {
          setSession(null)
          setRole('user')
          setError('')
          setLoading(false) // ensure spinner disappears after logout
          navigate('/login', { replace: true })
        }
      })
      unsub = data.subscription
    }
    init()
    return () => unsub?.unsubscribe()
  }, [checkSession, navigate, location.pathname])

  // Optimistic login (prevents “stuck until refresh”)
  const handleLogin = async () => {
    setError('')
    const emailTrim = (email || '').trim()
    const passwordTrim = (password || '').trim()
    if (!emailTrim || !passwordTrim) {
      setError('Please enter email and password.')
      return
    }

    try {
      setAuthBusy(true)
      const { error } = await supabase.auth.signInWithPassword({
        email: emailTrim,
        password: passwordTrim,
      })
      if (error) {
        setError('Login failed: ' + error.message)
        return
      }

      const { data: { session: s } } = await supabase.auth.getSession()
      if (s?.user) {
        setSession(s)
        setLoading(false) // ensure UI renders
        navigate('/dashboard', { replace: true }) // immediate navigation
        checkSession() // then validate approval/expiry
      } else {
        setError('Logged in but no session found.')
      }
    } catch (e) {
      console.error(e)
      setError('Login failed unexpectedly. Please try again.')
    } finally {
      setAuthBusy(false)
    }
  }

  const handleRegister = async () => {
    setError('')
    try {
      setAuthBusy(true)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } }
      })
      if (error) {
        setError('Registration failed: ' + error.message)
      } else {
        const userId = data?.user?.id
        if (userId) {
          await supabase.from('approved_users').insert([{
            user_id: userId,
            email,
            name,
            role: 'user',
            is_approved: false,
            // store date-only string
            expires_at: dayjs().add(1, 'year').format('YYYY-MM-DD'),
          }])
        }
        setError('Registration successful! Wait for approval.')
        setShowRegister(false)
      }
    } catch (e) {
      console.error(e)
      setError('Registration failed unexpectedly.')
    } finally {
      setAuthBusy(false)
    }
  }

  const handleGoogleLogin = async () => {
    setError('')
    try {
      setAuthBusy(true)
      const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' })
      if (error) setError('Google sign-in failed: ' + error.message)
    } catch (e) {
      console.error(e)
      setError('Google sign-in failed unexpectedly.')
    } finally {
      setAuthBusy(false)
    }
  }

  const handleLogout = async () => {
    try {
      setAuthBusy(true)
      await supabase.auth.signOut()
    } finally {
      setAuthBusy(false)
      setSession(null)
      setRole('user')
      setEmail('')
      setPassword('')
      setName('')
      setLoading(false)   // be explicit
      navigate('/login', { replace: true })
    }
  }

  if (!session && loading) return <SpinnerFullscreen />

  if (!session) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <LoginView
              error={error} setError={setError}
              showRegister={showRegister} setShowRegister={setShowRegister}
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              name={name} setName={setName}
              onLogin={handleLogin}
              onRegister={handleRegister}
              onGoogleLogin={handleGoogleLogin}
              busy={authBusy}
            />
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route element={<Layout role={role} onLogout={handleLogout} />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<AdminDashboard />} />
        <Route path="/vehicles" element={<VehicleList />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/missing" element={<MissingPaymentsReport />} />
        <Route path="/reports/profit" element={<ProfitLossReport />} />
        <Route path="/reports/specific" element={<SpecificVehicleReport />} />
        {role === 'admin' && <Route path="/admin/approvals" element={<UserApprovals />} />}
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}